/**
 * A fraude na conferência (`OB-01`, ADR-066): o operador suspeita, a supervisão confirma.
 *
 * Duas coisas que a tela precisa saber e que não são regra — a regra (quem pode, o que
 * falta) é do domínio, e chega pronta em `acoes_de_validacao` e na recusa do servidor:
 *
 * - **a lista dos motivos**, com o rótulo de cada um — a mesma de
 *   `transit_domain.MotivoDeFraude`, da Portaria ARTESP nº 81/2024;
 * - **as vistas** que podem provar a fraude: as imagens de pista disponíveis e as fotos do
 *   upload, as mesmas que o banco aceita como evidência (0059).
 */
import type {
  DecisaoRegistrada,
  DetalheDoTransito,
  MotivoDeFraude,
} from "../api/tipos";

/**
 * O que a tela diz ao lado da escolha. **Sem a portaria:** a norma que define os motivos é do
 * regulador de um estado, e a tela é de qualquer concessão — citar o regulador de outra seria
 * contar à concessionária com quem mais trabalhamos (revisão geral de 30/10, `N228`).
 */
export const FONTE_DOS_MOTIVOS =
  "Os motivos que a regulação do pedágio de livre passagem prevê";

export const MOTIVOS_DE_FRAUDE: { valor: MotivoDeFraude; rotulo: string }[] = [
  { valor: "PLACA_COBERTA", rotulo: "Placa coberta" },
  { valor: "PLACA_ADULTERADA", rotulo: "Placa adulterada" },
  { valor: "PLACA_CLONADA", rotulo: "Placa clonada" },
  { valor: "PLACA_RETIRADA", rotulo: "Placa retirada" },
];

export function rotuloDoMotivo(motivo: MotivoDeFraude): string {
  return MOTIVOS_DE_FRAUDE.find((m) => m.valor === motivo)?.rotulo ?? motivo;
}

export interface VistaDaFraude {
  id: string;
  rotulo: string;
}

const CAMERA: Record<string, string> = {
  FRONTAL: "Câmera frontal",
  LATERAL: "Câmera lateral",
  EIXO: "Câmera de eixo",
};

/**
 * As imagens deste trânsito que podem ir ao relatório como prova: a de pista só quando
 * disponível (a indisponível não tem foto), e cada foto do upload pela vista dela.
 */
export function vistasDoTransito(detalhe: DetalheDoTransito): VistaDaFraude[] {
  const pista = detalhe.leituras
    .filter((l) => l.disponivel && l.imagem_id)
    .map((l) => ({
      id: l.imagem_id as string,
      rotulo: CAMERA[l.camera] ?? `Câmera ${l.camera.toLowerCase()}`,
    }));
  const fotos = (detalhe.imagens_de_analise ?? []).map((v) => ({
    id: v.imagem_id,
    rotulo: `Foto: ${v.vista} (${v.nome_original})`,
  }));
  return [...pista, ...fotos];
}

/**
 * A suspeita em aberto: a última decisão do trânsito, quando foi ela. A confirmação nasce
 * com o motivo e a vista que o operador escolheu — a supervisão confere, e muda se quiser.
 */
export function suspeitaAberta(
  detalhe: DetalheDoTransito,
): DecisaoRegistrada | null {
  const ultima = detalhe.decisoes[detalhe.decisoes.length - 1];
  return ultima?.acao === "SUSPEITAR_FRAUDE" ? ultima : null;
}

/** O que a trilha diz de uma decisão de fraude: o motivo e a vista, pelo nome. */
export function linhaDaFraude(
  decisao: DecisaoRegistrada,
  vistas: VistaDaFraude[],
): string | null {
  if (!decisao.motivo_de_fraude) return null;
  const vista = vistas.find((v) => v.id === decisao.evidencia_id);
  return `fraude: ${rotuloDoMotivo(decisao.motivo_de_fraude)} · vista: ${vista?.rotulo ?? "imagem do trânsito"}`;
}
